import type { SelectedProduct } from "@credit-acceptance/step6-bff-client";
import { z } from "zod";

// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const REQUIRED_MESSAGE = "This is a required field.";

/** Sentinel `termMonths` takes when "Unlimited Months?" is checked — real value, never shown to the user. */
export const UNLIMITED_MONTH_SENTINEL = "999";
/** Sentinel `termMiles` takes when "Unlimited Miles?" is checked — real value, never shown to the user. */
export const UNLIMITED_MILE_SENTINEL = "999999";

/** No live vendor-lookup service exists here — a small fixed mock list stands in for `dealerSetupService.fetchAncillaryProductVscVendorMap(...)`. */
export const TPAP_VENDOR_OPTIONS = ["WYNNS-WYN08", "ZURICH-VSC", "ALLY-PREMIER"] as const;

/** The "Select an Option" entry is itself a real, selectable list item (not a `SelectValue` placeholder) — matches the legacy dropdown's actual shape. Label is translated in the component, alongside the other Form Number options. */
export const FORM_NUMBER_SELECT_AN_OPTION = "__select_an_option__";
/** Trailing sentinel — counts as a complete (non-blank) selection, same as any real form number. Label is translated in the component. */
export const FORM_NUMBER_NOT_LISTED = "__not_listed__";

/** Mocked in place of `getApprovedAncillaryForms`/`getSelectedAncillaryForms` — no such service exists here. */
export const FORM_NUMBER_CODES = ["FORM-2024-A", "FORM-2024-B", "FORM-2024-C"] as const;

// ---------------------------------------------------------------------------
// vehicleServiceContractInformationSchema — RHF + Zod for
// VehicleServiceContractInformation.tsx. `SelectedProduct` only has
// vendorName/certificateNumber/sellingPrice/addendumNumber/selectedProductName
// — Term, Effective Date, Form Number, and the TPAP-vs-non-TPAP vendor split
// have no backend source at all, so `tpapVsc` (mocked true) and everything it
// gates are invented, same spirit as ContractVehicleInformation's CPO fields
// and InsuranceInformation's mocked Full Coverage.
//
// Deliberately NOT modeled (no equivalent concept exists anywhere in this
// codebase, matching this session's precedent of skipping PROGRAM_TYPE/COLON
// and DPSC role-gating): the "match original value on file" checks for
// Selling Price/Term Months/Term Miles/Effective Date (needs a tracked
// "original" + a correction-letter feature flag), the AH-deal-status special
// certificate requirement, and every preAppDeal/ecDeal/vscEnable disable
// nuance — those all collapse to the plain `editable` toggle here.
// ---------------------------------------------------------------------------
export const vehicleServiceContractInformationSchema = z
  .object({
    // Never user-editable and never validated — just reflects whatever drives
    // the TPAP-vs-non-TPAP branching below (see the superRefine).
    tpapVsc: z.boolean(),
    // Non-TPAP pure display counterpart to `tpapVendor` — never validated.
    vendorDisplay: z.string(),
    // "Not directly enforced by required-field validation" per spec.
    tpapVendor: z.string(),
    certificateNumber: z.string(),
    // CurrencyField's onChange already emits `number | undefined` for an empty
    // input — no emptyStringToUndefined preprocessing needed here.
    sellingPrice: z.number().optional(),
    // Non-TPAP pure display counterpart to Term Months/Miles — never validated.
    termDisplay: z.string(),
    isUnlimitedMonth: z.boolean(),
    termMonths: z.string(),
    isUnlimitedMile: z.boolean(),
    termMiles: z.string(),
    effectiveDate: z.string(),
    formNumber: z.string(),
  })
  .superRefine((data, context) => {
    if (!data.tpapVsc) {
      return;
    }
    if (!data.certificateNumber) {
      context.addIssue({ code: "custom", path: ["certificateNumber"], message: REQUIRED_MESSAGE });
    }
    if (data.sellingPrice === undefined) {
      context.addIssue({ code: "custom", path: ["sellingPrice"], message: REQUIRED_MESSAGE });
    }
    if (!data.isUnlimitedMonth && !data.termMonths) {
      context.addIssue({ code: "custom", path: ["termMonths"], message: REQUIRED_MESSAGE });
    }
    if (!data.isUnlimitedMile && !data.termMiles) {
      context.addIssue({ code: "custom", path: ["termMiles"], message: REQUIRED_MESSAGE });
    }
    if (!data.effectiveDate) {
      context.addIssue({ code: "custom", path: ["effectiveDate"], message: REQUIRED_MESSAGE });
    }
    if (!data.formNumber || data.formNumber === FORM_NUMBER_SELECT_AN_OPTION) {
      context.addIssue({ code: "custom", path: ["formNumber"], message: REQUIRED_MESSAGE });
    }
  });

export type VehicleServiceContractInformationInput = z.infer<typeof vehicleServiceContractInformationSchema>;

/**
 * Builds RHF defaultValues from the real `SelectedProduct` prop where a real
 * field exists (vendorName/certificateNumber/sellingPrice); everything else
 * (tpapVsc, tpapVendor, term months/miles, effective date, form number) is
 * mocked — no backend source exists yet — seeded with valid, non-required-error
 * values so the mocked "Yes" (TPAP) branch is demoable today.
 * @param vsc The dashboard response's `SelectedProduct` for VSC, if any.
 * @returns {VehicleServiceContractInformationInput} Seed values for `useForm`'s `defaultValues`.
 */
export function buildVscDefaultValues(vsc?: SelectedProduct): VehicleServiceContractInformationInput {
  return {
    tpapVsc: true,
    vendorDisplay: vsc?.vendorName ?? "",
    tpapVendor: TPAP_VENDOR_OPTIONS[0],
    certificateNumber: vsc?.certificateNumber ?? "",
    sellingPrice: vsc?.sellingPrice ?? 2500,
    termDisplay: "",
    isUnlimitedMonth: false,
    termMonths: "60",
    isUnlimitedMile: false,
    termMiles: "60000",
    effectiveDate: "2024-01-15",
    formNumber: FORM_NUMBER_CODES[0],
  };
}
